const ROOT_SELECTOR = '[data-xsto-product-gallery]';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function pauseSlide(slide) {
  slide.querySelectorAll('video').forEach((video) => {
    if (!video.paused) video.pause();
  });

  slide.querySelectorAll('iframe').forEach((frame) => {
    const host = frame.dataset.videoType || frame.src;
    let origin;
    try {
      origin = new URL(frame.src, window.location.href).origin;
    } catch {
      return;
    }

    if (host.includes('youtube')) {
      frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
    } else if (host.includes('vimeo')) {
      frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), origin);
    }
  });
}

function initGallery(root) {
  if (root.dataset.galleryReady === 'true') return;

  const viewport = root.querySelector('[data-gallery-viewport]');
  const slides = [...root.querySelectorAll('[data-gallery-slide]')];
  if (!viewport || slides.length < 1) return;

  root.dataset.galleryReady = 'true';
  const controls = root.querySelector('[data-gallery-controls]');
  const previous = root.querySelector('[data-gallery-prev]');
  const next = root.querySelector('[data-gallery-next]');
  const status = root.querySelector('[data-gallery-status]');
  const thumbnails = [...root.querySelectorAll('[data-gallery-thumbnail]')];
  let activeIndex = 0;
  let frameRequest = 0;
  let visibilityFrameRequest = 0;

  if (slides.length > 1) {
    if (controls) controls.hidden = false;
    const thumbnailRail = root.querySelector('[data-gallery-thumbnails]');
    if (thumbnailRail) thumbnailRail.hidden = false;
  }

  const pauseInactiveSlides = () => {
    slides.forEach((slide, index) => {
      if (index !== activeIndex) pauseSlide(slide);
    });
  };

  const renderActiveSlide = () => {
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-current', String(index === activeIndex));
    });
    if (status) status.textContent = `${activeIndex + 1} / ${slides.length}`;
    if (previous) previous.disabled = activeIndex === 0;
    if (next) next.disabled = activeIndex === slides.length - 1;
  };

  const updateActiveSlide = () => {
    frameRequest = 0;
    const viewportLeft = viewport.getBoundingClientRect().left;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - viewportLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (activeIndex !== closestIndex) {
      activeIndex = closestIndex;
      pauseInactiveSlides();
    }
    renderActiveSlide();
  };

  const scheduleActiveSlideUpdate = () => {
    if (!frameRequest) frameRequest = window.requestAnimationFrame(updateActiveSlide);
  };

  const pauseMediaOutsideViewport = () => {
    visibilityFrameRequest = 0;
    slides.forEach((slide, index) => {
      if (!slide.querySelector('video, iframe')) return;
      if (index !== activeIndex) {
        pauseSlide(slide);
        return;
      }

      const rect = slide.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const visibleRatio = rect.width && rect.height ? (visibleWidth * visibleHeight) / (rect.width * rect.height) : 0;
      if (visibleRatio < 0.6) pauseSlide(slide);
    });
  };

  const scheduleMediaVisibilityCheck = () => {
    if (!visibilityFrameRequest) visibilityFrameRequest = window.requestAnimationFrame(pauseMediaOutsideViewport);
  };

  const goTo = (index) => {
    const boundedIndex = Math.max(0, Math.min(index, slides.length - 1));
    const slide = slides[boundedIndex];
    const left = viewport.scrollLeft + slide.getBoundingClientRect().left - viewport.getBoundingClientRect().left;
    viewport.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    activeIndex = boundedIndex;
    renderActiveSlide();
    pauseInactiveSlides();
  };

  previous?.addEventListener('click', () => goTo(activeIndex - 1));
  next?.addEventListener('click', () => goTo(activeIndex + 1));
  thumbnails.forEach((thumbnail, index) => {
    thumbnail.addEventListener('click', () => goTo(index));
  });

  viewport.addEventListener('scroll', scheduleActiveSlideUpdate, { passive: true });
  window.addEventListener('scroll', scheduleMediaVisibilityCheck, { passive: true, capture: true });
  window.addEventListener('resize', scheduleMediaVisibilityCheck, { passive: true });
  viewport.addEventListener('keydown', (event) => {
    if (event.target !== viewport || slides.length < 2) return;

    let targetIndex;
    if (event.key === 'ArrowRight') targetIndex = activeIndex + 1;
    else if (event.key === 'ArrowLeft') targetIndex = activeIndex - 1;
    else if (event.key === 'Home') targetIndex = 0;
    else if (event.key === 'End') targetIndex = slides.length - 1;
    else return;

    event.preventDefault();
    goTo(targetIndex);
  });

  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) pauseSlide(entry.target);
        });
      }, { root: null, threshold: [0, 0.6, 1] })
    : null;

  if (visibilityObserver) {
    slides.forEach((slide) => visibilityObserver.observe(slide));
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) slides.forEach(pauseSlide);
  });

  updateActiveSlide();
}

function initGalleries(scope = document) {
  scope.querySelectorAll(ROOT_SELECTOR).forEach(initGallery);
}

initGalleries();
document.addEventListener('shopify:section:load', (event) => initGalleries(event.target));
